import React, { ComponentProps } from 'react';
import styled, { css } from 'styled-components';
import MockPrintRadioButton from './MockPrintRadioButton';
import { HBRadioButtonCustomTheme } from './HBRadioButton.types';
import { MustIncludeOneOfTestIdProps } from '../common/internal/test-id.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { getControlStateStyles } from '../HBCheckbox/selectionControlUtilities';
import {
    inlineFlexCenterAll,
    hideInPrint,
    focusState
} from '../../styles/mixins';
import { hoverEase } from '../../styles/variables';
import { vars } from '../../theme';
import { TControlSize } from '../../tokens/types';
import { RADIO_SIZES } from './HBRadioButton.types';

/* ------------------------------ Types ------------------------------ */
export interface IRadioButtonBase
    extends Omit<ComponentProps<'input'>, 'size'> {
    /** Controls the radio circle dimensions. 'sm' = 16px, 'md' = 20px, 'lg' = 24px. @default 'md' */
    size?: TControlSize;
    /** Custom color theme that overrides the default border, background, and checked-state colors of the radio button. */
    customTheme?: HBRadioButtonCustomTheme;
    /** Applies error styling: red border when unchecked, red fill when checked. */
    error?: boolean;
    /** Groups this radio button with others sharing the same name; only one in the group can be selected at a time. */
    name?: string;
}

export type HBRadioButtonProps = IRadioButtonBase & MustIncludeOneOfTestIdProps;
/* ------------------------------ Component ------------------------------ */
const HBRadioButton = ({
    value,
    id: idFromProps,
    name,
    checked,
    disabled,
    error,
    size = 'md',
    customTheme,
    className,
    tabIndex,
    testId,
    testIdName,
    onChange,
    onBlur
}: HBRadioButtonProps) => {
    const id = idFromProps ?? String(value).replace(/ /g, '_');
    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName || id || name,
        suffix: 'radio-button',
        componentName: 'HBRadioButton'
    });

    return (
        <>
            <RadioButton isDisabled={disabled} $customTheme={customTheme}>
                <Input
                    type="radio"
                    id={id}
                    name={name}
                    value={value}
                    radioSize={size}
                    disabled={disabled}
                    checked={checked}
                    aria-checked={checked}
                    onChange={onChange}
                    className={className}
                    onBlur={onBlur}
                    data-testid={finalTestId}
                    tabIndex={tabIndex}
                    error={error}
                    customTheme={customTheme}
                />
            </RadioButton>

            <MockPrintRadioButton
                size={size}
                color={customTheme?.background}
                checked={checked}
            />
        </>
    );
};

export default HBRadioButton;

/* ------------------------------ Styles ------------------------------ */
export const Input = styled.input<{
    radioSize: TControlSize;
    checked: boolean;
    error?: boolean;
    customTheme?: HBRadioButtonCustomTheme;
}>`
    cursor: pointer;
    appearance: none;
    margin: 0 !important;
    ${inlineFlexCenterAll};
    background-color: transparent;
    width: ${({ radioSize }) => RADIO_SIZES[radioSize].outerCircle};
    height: ${({ radioSize }) => RADIO_SIZES[radioSize].outerCircle};
    border-width: 1px;
    border-style: solid;
    box-sizing: border-box;
    border-radius: ${vars.radius.full};
    transition: background-color ${hoverEase[0]} ${hoverEase[1]},
        border-color ${hoverEase[0]} ${hoverEase[1]};

    ${({ checked, disabled, error, customTheme }) => {
        return getControlStateStyles({
            checked,
            disabled,
            error,
            customTheme
        });
    }};

    &:after {
        content: '';
        border-radius: inherit;
        width: ${({ radioSize }) => RADIO_SIZES[radioSize].innerCircle};
        height: ${({ radioSize }) => RADIO_SIZES[radioSize].innerCircle};

        ${({ checked, disabled, error }) => {
            return css`
                background-color: ${error && !disabled
                    ? checked
                        ? vars.colors.base.danger
                        : vars.colors.surface.danger
                    : vars.colors.surface.item};
            `;
        }}
    }
    ${focusState()}
`;

const RadioButton = styled.div<{
    isDisabled: boolean;
    $customTheme?: HBRadioButtonCustomTheme;
}>`
    ${hideInPrint};
    display: inline-flex;
    align-items: center;
    ${({ isDisabled }) => isDisabled && 'pointer-events: none;'}

    &&& input:disabled:checked {
        background: ${({ $customTheme }) =>
            $customTheme?.background || vars.colors.surface.disabled};
    }
`;
